import { useState } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import {
  Users,
  Cpu,
  Settings,
  Rocket,
  TrendingUp,
  Building,
  ArrowRight,
  CheckCircle,
  Mail,
  Linkedin } from
"lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Company Logo
const COMPANY_LOGO = "/logo.png";

// Hero Background Image
const HERO_BG = "https://images.unsplash.com/photo-1762279388956-1c098163a2a8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGRhcmslMjBibHVlJTIwdGVjaG5vbG9neSUyMG5ldHdvcmslMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc3NDI5OTQ5M3ww&ixlib=rb-4.1.0&q=85";

// Principal Portrait
const PRINCIPAL_IMG = "/randy.png";

// Michelle Waters Portrait
const MICHELLE_IMG = "/michelle.png";

const principals = [
{
  name: "Randy Waters",
  image: PRINCIPAL_IMG,
  bio: "Randy Waters brings over 30 years of executive leadership, product management, project management, and data science expertise to healthcare, sports technology, IT, and network domains. His extensive experience spans from startup ventures to Fortune 500 companies, driving innovation and delivering measurable results.",
  features: [
  "30+ Years Executive Leadership",
  "Healthcare and Mental Health Technology",
  "Sports Technology",
  "Product Management",
  "Project Management",
  "Data Science & Analytics"],

  linkedin: "https://www.linkedin.com/in/watersrandy/"
},
{
  name: "Michelle Waters",
  image: MICHELLE_IMG,
  bio: "Michelle Waters provides over two decades of residential real estate design and development expertise, bringing innovative designs that are ahead of coming trends and complement the neighboring community. Her vision transforms properties into harmonious spaces that enhance both value and livability.",
  features: [
  "20+ Years Real Estate Development",
  "Innovative Trend-Forward Designs",
  "Community-Focused Development",
  "Property Value Enhancement"],

  linkedin: null
}];


const services = [
{
  id: 1,
  title: "Leadership Consulting",
  description: "Executive coaching and strategic leadership development to drive organizational transformation.",
  icon: Users
},
{
  id: 2,
  title: "Technology Advisory",
  description: "Technology strategy, digital transformation, and IT infrastructure optimization.",
  icon: Cpu
},
{
  id: 3,
  title: "Operations Advisory",
  description: "Streamline operations, improve efficiency, and optimize business processes.",
  icon: Settings
},
{
  id: 4,
  title: "Startup Advisory",
  description: "From ideation to scale\u2014product strategy, go-to-market, and growth consulting. \xA0Provide sight around upcoming corners.",
  icon: Rocket
},
{
  id: 5,
  title: "Capital Raise",
  description: "Fundraising strategy, investor relations, and capital structure optimization.",
  icon: TrendingUp
},
{
  id: 6,
  title: "Residential Real Estate Design and Development",
  description: "Commercial real estate strategy, design consulting, and transaction advisory.",
  icon: Building
}];


// Hero Section Component
const HeroSection = () => {
  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToServices = () => {
    document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="hero-section" data-testid="hero-section">
      <div
        className="hero-bg"
        style={{ backgroundImage: `url(${HERO_BG})` }} />

      <div className="hero-overlay" />
      <div className="hero-glow" />
      
      <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
        <div className="animate-fade-in-up opacity-0" style={{ animationDelay: '0.1s', animationFillMode: 'forwards' }}>
          <img
            src={COMPANY_LOGO}
            alt="South Bay Innovations LLC"
            className="h-32 md:h-40 mx-auto mb-8 rounded-xl"
            data-testid="company-logo" />

        </div>
        
        <h1
          className="text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight text-white mb-6 animate-fade-in-up opacity-0"
          style={{ animationDelay: '0.2s', animationFillMode: 'forwards' }}
          data-testid="hero-title">

          South Bay Innovations
          <span className="block text-[#D4AF37] mt-2">LLC</span>
        </h1>
        
        <p
          className="text-lg md:text-xl text-[#94A3B8] max-w-2xl mx-auto mb-4 animate-fade-in-up opacity-0"
          style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}
          data-testid="hero-tagline">

          Strategic Innovation. Executive Leadership.
        </p>
        
        <p
          className="text-base md:text-lg text-[#64748B] max-w-xl mx-auto mb-10 animate-fade-in-up opacity-0"
          style={{ animationDelay: '0.35s', animationFillMode: 'forwards' }}>

          Empowering businesses through expert consulting in technology, operations, and capital growth.
        </p>
        
        <div
          className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-up opacity-0"
          style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}>

          <button
            onClick={scrollToContact}
            className="btn-primary transition-transform-opacity"
            data-testid="cta-get-started">

            Get Started
            <ArrowRight className="w-4 h-4" />
          </button>
          <button
            onClick={scrollToServices}
            className="btn-secondary transition-colors-border"
            data-testid="cta-our-services">

            Our Services
          </button>
        </div>
      </div>
    </section>);

};

// Services Section Component
const ServicesSection = () => {
  return (
    <section id="services" className="section-container" data-testid="services-section">
      <div className="text-center mb-16">
        <span className="badge mb-4">What We Offer</span>
        <h2 className="text-3xl md:text-5xl font-semibold text-white mb-4">
          Our Services
        </h2>
        <p className="text-[#94A3B8] max-w-2xl mx-auto text-base md:text-lg">
          Comprehensive consulting solutions tailored to accelerate your business growth.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {services.map((service) => {
          const IconComponent = service.icon;
          return (
            <div
              key={service.id}
              className="service-card transition-transform-opacity"
              data-testid={`service-card-${service.id}`}>

              <div className="service-icon">
                <IconComponent className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">
                {service.title}
              </h3>
              <p className="text-[#94A3B8] text-sm leading-relaxed">
                {service.description}
              </p>
            </div>);

        })}
      </div>
    </section>);

};

// Principal Section Component
const PrincipalSection = () => {
  return (
    <section className="principal-section" data-testid="principal-section">
      <div className="section-container">
        <div className="text-center mb-16">
          <span className="badge mb-4">Meet Our Principals</span>
          <h2 className="text-3xl md:text-5xl font-semibold text-white mb-4">
            Leadership Team
          </h2>
          <p className="text-[#94A3B8] max-w-2xl mx-auto text-base md:text-lg">
            Decades of combined expertise driving innovation and delivering results.
          </p>
        </div>

        <div className="space-y-24">
          {principals.map((principal, index) =>
          <div
            key={principal.name}
            className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
            data-testid={`principal-card-${index}`}>

              {/* Image */}
              <div className={`principal-image aspect-[4/5] max-w-md mx-auto ${index % 2 === 1 ? 'lg:order-2' : 'lg:order-1'}`}>
                <img
                src={principal.image}
                alt={`${principal.name} - Principal`}
                className="w-full h-full object-cover"
                data-testid={`principal-image-${index}`} />

              </div>
              
              {/* Content */}
              <div className={`${index % 2 === 1 ? 'lg:order-1 lg:pr-8' : 'lg:order-2 lg:pl-8'}`}>
                <h3 className="text-3xl md:text-4xl font-semibold text-white mb-6">
                  {principal.name}
                </h3>
                <p className="text-[#94A3B8] text-base md:text-lg mb-8 leading-relaxed">
                  {principal.bio}
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                  {principal.features.map((feature, featureIndex) =>
                <div key={featureIndex} className="feature-tag">
                      <CheckCircle className="w-4 h-4 text-[#3B82F6]" />
                      <span>{feature}</span>
                    </div>
                )}
                </div>
                
                {principal.linkedin &&
              <a
                href={principal.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary inline-flex"
                data-testid={`linkedin-link-${index}`}>

                    <Linkedin className="w-5 h-5" />
                    View LinkedIn Profile
                  </a>
              }
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

};

// Contact Section Component
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully! We\'ll be in touch soon.');
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="contact-section" data-testid="contact-section">
      <div className="section-container">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left Content */}
          <div>
            <span className="badge mb-4">Get In Touch</span>
            <h2 className="text-3xl md:text-5xl font-semibold text-white mb-6">
              Let's Start a Conversation
            </h2>
            <p className="text-[#94A3B8] text-base md:text-lg mb-8 leading-relaxed">
              Ready to transform your business? Whether you need strategic guidance, 
              technology expertise, or capital advisory—we're here to help you succeed.
            </p>
            
            <div className="space-y-4">
              <a
                href="mailto:contact@southbayinnovations.com"
                className="feature-tag inline-flex hover:border-[#3B82F6]/50 transition-colors-border"
                data-testid="email-link">

                <Mail className="w-4 h-4 text-[#3B82F6]" />
                contact@southbayinnovations.com
              </a>
            </div>
          </div>
          
          {/* Contact Form */}
          <div className="glass-card p-8">
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-[#E2E8F0] mb-2">
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your name"
                  className="form-input transition-colors-border"
                  data-testid="input-name" />

              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-[#E2E8F0] mb-2">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  className="form-input transition-colors-border"
                  data-testid="input-email" />

              </div>
              
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-[#E2E8F0] mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="How can we help you?"
                  className="form-input form-textarea transition-colors-border"
                  data-testid="input-message" />

              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full justify-center transition-transform-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-button">

                {isSubmitting ? 'Sending...' : 'Send Message'}
                {!isSubmitting && <ArrowRight className="w-4 h-4" />}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>);

};

// Footer Component
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="footer py-8" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 text-center">
        <p className="text-[#64748B] text-sm">
          © {currentYear} South Bay Innovations LLC. All rights reserved.
        </p>
      </div>
    </footer>);

};

// Main App Component
function App() {
  return (
    <div className="min-h-screen bg-[#0A0A0B]">
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: '#121214',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            color: '#FAFAFA'
          }
        }} />

      <HeroSection />
      <ServicesSection />
      <PrincipalSection />
      <ContactSection />
      <Footer />
    </div>);

}

export default App;